import { MinusCircleOutlined } from "@ant-design/icons";
import { Evidence } from "@p0security/shared/types/evidence";
import { Button, Empty, List, Spin, Typography, message } from "antd";
import { GraphTooltip } from "components/GraphTooltip";
import { useAuthFetch } from "components/Login/hook";
import { formatDistance } from "date-fns";
import { toLower } from "lodash";
import { FirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useState } from "react";

import { PreApprovalPreview } from "./PreApprovalPreview";

const REVOKED_PRE_APPROVAL_SUCCESS = "Pre-approval revoked successfully.";
const REVOKED_PRE_APPROVAL_FAILURE = "Failed to revoke pre-approval.";

export const TitledPreApprovalList: React.FC<
  {
    title: string;
    loading: boolean;
  } & PreApprovalListProps
> = ({ title, loading, ...listProps }) => (
  <>
    <Typography.Title level={4}>{title}</Typography.Title>
    {loading ? (
      <Spin />
    ) : listProps.preApproval.length > 0 ? (
      <PreApprovalList {...listProps} />
    ) : (
      <Empty description={`No ${toLower(title)} pre approval`} />
    )}
  </>
);

type PreApprovalListProps = {
  preApproval: FirestoreDoc<Evidence>[];
};

const PreApprovalList: React.FC<PreApprovalListProps> = ({ preApproval }) => {
  const [dirty, setDirty] = useState<boolean>(false);
  const authFetch = useAuthFetch();

  const handleRevoke = useCallback(
    async (evidenceId: string) => {
      try {
        setDirty(true);
        const response = await authFetch(`evidence/${evidenceId}/revoke`, {
          method: "POST",
        });

        if (response?.ok) {
          message.success(REVOKED_PRE_APPROVAL_SUCCESS);
        } else {
          message.error(REVOKED_PRE_APPROVAL_FAILURE);
        }
      } catch (error) {
        message.error(REVOKED_PRE_APPROVAL_FAILURE);
      } finally {
        setDirty(false);
      }
    },
    [authFetch]
  );

  const renderPreApproval = useCallback(
    (item: FirestoreDoc<Evidence>) => {
      return (
        <List.Item
          actions={[
            item.data.startsAt > Date.now() ? (
              <Typography.Text key="expiry" type="secondary">
                Starts in{" "}
                {item.data.startsAt &&
                  formatDistance(Date.now(), item.data.startsAt)}
              </Typography.Text>
            ) : null,
            <Typography.Text key="expiry" type="secondary">
              Expires in{" "}
              {item.data.endsAt && formatDistance(item.data.endsAt, Date.now())}
            </Typography.Text>,
            <GraphTooltip
              key="revoke"
              title={
                dirty
                  ? "Pre approval is being revoked..."
                  : !!item.data.revokedAt
                    ? "Pre approval has already been revoked."
                    : "Revoke grant from user"
              }
            >
              <Button
                key="revoke"
                type="default"
                // In a useCallback already
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => handleRevoke(item.id)}
                disabled={!!item.data.revokedAt || dirty}
              >
                <MinusCircleOutlined /> Revoke
              </Button>
            </GraphTooltip>,
          ]}
        >
          <PreApprovalPreview preApproval={item.data} />
        </List.Item>
      );
    },
    [dirty, handleRevoke]
  );

  return (
    <List
      dataSource={preApproval}
      renderItem={renderPreApproval}
      pagination={false}
    />
  );
};
